import * as React from 'react'
import { Box, Card, CardContent, Chip, Stack, Typography } from '@mui/material'
import useAutoTranslate from '../hooks/useAutoTranslate'

const SKIP_TYPES = [
    { code: 'MS1', amount: 1, unit: 'min' },
    { code: 'MS2', amount: 5, unit: 'min' },
    { code: 'MS3', amount: 10, unit: 'min' },
    { code: 'MS4', amount: 30, unit: 'min' },
    { code: 'MS5', amount: 1, unit: 'h' },
    { code: 'MS6', amount: 5, unit: 'h' },
    { code: 'MS7', amount: 24, unit: 'h' }
]
const formatter = new Intl.NumberFormat('en-US')
export const isBthAccount = user => ['battle_for_the_throne', 'battle for the throne', 'battleforthethrone', 'bth']
    .includes(String(user?.externalEventType ?? '').trim().toLowerCase())
const skipCount = value => {
    if (!['number', 'string'].includes(typeof value) || String(value).trim() === '') return undefined
    const count = Number(value)
    return Number.isSafeInteger(count) && count >= 0 ? count : undefined
}

function SkipHourglass({ amount, unit }) {
    const color = unit === 'min' ? '#a3cf58' : '#c78be9'
    return (
        <Box component="svg" viewBox="0 0 44 46" aria-hidden="true" sx={{ width: 40, height: 42, display: 'block' }}>
            <path d="M9 5h26v5c0 6-5 10-9 13 4 3 9 7 9 13v5H9v-5c0-6 5-10 9-13-4-3-9-7-9-13z"
                fill={color} fillOpacity="0.16" stroke={color} strokeWidth="2" />
            <path d="M6 5h32M6 41h32" stroke={color} strokeWidth="4" strokeLinecap="round" />
            <path d="M13 12h18l-9 9zM13 36l9-9 9 9z" fill={color} fillOpacity="0.45" />
            <text x="22" y="29" textAnchor="middle" fill="#f8fafc" fontFamily="inherit" fontWeight="800" fontSize={amount >= 10 ? '20' : '24'}
                stroke="#172119" strokeWidth="2" paintOrder="stroke">{amount}</text>
        </Box>
    )
}

export default function BthSkipInventoryCard({ user, status, appLanguage = 'en' }) {
    const t = useAutoTranslate(appLanguage, ['Time skips', 'Live', 'Last received', 'Waiting for inventory', 'Bot stopped'])
    if (!isBthAccount(user)) return null

    const inventory = status?.skipInventory
    const hasInventory = SKIP_TYPES.some(({ code }) => skipCount(inventory?.[code]) !== undefined)
    const running = Number(user?.state) === 1
    const live = running && hasInventory
    const label = live ? t('Live') : hasInventory ? t('Last received') : t('Waiting for inventory')

    return (
        <Card data-testid="bth-skip-inventory-card" data-no-ui-translate="true" sx={{ borderRadius: 3, overflow: 'hidden' }}>
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ mb: 1.5 }}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <Chip label="BTH" size="small" sx={{ fontWeight: 800, color: '#d8b4fe', backgroundColor: 'rgba(168,85,247,0.13)' }} />
                        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{t('Time skips')}</Typography>
                    </Stack>
                    <Stack direction="row" alignItems="center" spacing={0.6}>
                        <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: live ? '#86efac' : '#94a3b8' }} />
                        <Typography variant="caption" sx={{ color: live ? '#86efac' : 'text.secondary' }}>{label}</Typography>
                    </Stack>
                </Stack>
                <Box role="group" aria-label={`BTH ${t('Time skips')}`} aria-live="polite" aria-atomic="true"
                    sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))', gap: { xs: 0.25, sm: 0.75 } }}>
                    {SKIP_TYPES.map(({ code, amount, unit }) => {
                        const count = skipCount(inventory?.[code])
                        const value = count === undefined ? '—' : formatter.format(count)
                        return (
                            <Stack key={code} data-testid={`bth-skip-${code}`} alignItems="center" spacing={0.4}
                                sx={{ minWidth: 0, py: 0.5 }}>
                                <SkipHourglass amount={amount} unit={unit} />
                                <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: 10 }}>{amount} {unit}</Typography>
                                <Typography data-testid={`bth-skip-count-${code}`} aria-label={`${amount} ${unit}: ${value}`}
                                    sx={{ fontSize: { xs: 12, sm: 14 }, fontWeight: 800, fontVariantNumeric: 'tabular-nums',
                                        color: count === undefined || count === 0 ? 'text.secondary' : 'text.primary',
                                        maxWidth: '100%', overflowWrap: 'anywhere' }}>{value}</Typography>
                            </Stack>
                        )
                    })}
                </Box>
                {!running && <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'text.secondary' }}>{t('Bot stopped')}</Typography>}
            </CardContent>
        </Card>
    )
}
